import { useState, useEffect } from 'react';

import { Row, Col, Table, Space, Typography, Divider, Button, Collapse, DatePicker, Select, message, Modal, InputNumber, Tag } from 'antd';
import UpdateSpendMode from './UpdateSpendMode';
import { SUBSCRIPTION_STATUS, PAYMENT_PRODUCT_TYPE } from '../../../../constants/AppConstants';
import Data from '../../../CustomText/data';
import {
  convertCentsIntoDollar,
  formatDate,
  isSupportManager,
  isSupportLead,
  isSupportAgent2,
  isSupportAgent3
} from '../../../../helpers/genericHelper';
import API from '../../../../services/API';
import StatementView from './StatementView';
import TransactionHistoryComponent from '../TransactionHistory';
import PaymentLinkCheckoutModal from '../Payments/PaymentLinkCheckoutModal';
import RefundModal from '../RefundModal';
import VoidTransactionsModal from './VoidTransactionsModal'
import PaymentLinkPanel from '../Payments/PaymentLinksPanel';
import CreditLimitIncentivesComponent from "./CreditLimitIncentivesComponent";
import SpendLimitChangesComponent from "./SpendLimitChangesComponent";
import CliEligibilitySummary from "./CliEligibilitySummary";
import StatementsList from './StatementsList';
import DeclinedTransactionsComponent from '../DeclinedTransactions';
import MonthlySpendPowerComponent from '../MonthlySpendPower';
import PaymentHistoryComponent from '../PaymentHistory';
import PaymentAttemptsComponent from '../PaymentAttempts';
import ActiveSubscriptions from '../ActiveSubscriptions';
import { Document, PDFDownloadLink } from '@react-pdf/renderer';
import moment from 'moment';
import { CCStatement } from './Statement';
import AutoPayAction from '../Actions/AutoPayAction';
import useMerchantActivation from './useMerchantActivation';
import MerchantListModal from './MerchantListModal';
import { getAutoPayStatus } from '../../../../helpers/parsingHelper';
import { PersistedCollapse } from '../../../PersistedSections';

const { Text, Title } = Typography;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;

export default function AccountView({
  creditCard, setShowSpendPowerModal, userId, domain, userSummary,
  openChangeDueDateModal,
  showChargeSilaModal, showCancelPaymentModal, refreshList, paymentAttempts, autoPayStatus
}) {

  const account = creditCard;
  const card = creditCard;
  
  const [statements, setStatements] = useState([]);
  const [loadingStatements, setLoadingStatements] = useState(false);
  const [activity, setActivity] = useState([]);
  
  const [startDate, setStartDate] = useState(moment().subtract(6, 'months'));
  const [endDate, setEndDate] = useState(moment());

  const [merchantCycleLoading, setMerchantCycleLoading] = useState(false);
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [refundAmount, setRefundAmount] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [merchantListModalVisible, setMerchantListModalVisible] = useState(false);

  const merchantActivation = useMerchantActivation({ userId });
  const autoPayStatusData = getAutoPayStatus(autoPayStatus, PAYMENT_PRODUCT_TYPE.CREDIT_CARD);

  const loadStatementAndActivities = async () => {
    setLoadingStatements(true);
    try {
      setStatements(await API.getCreditCardStatements({ accountId: card?.accountId }));
      setActivity(await API.getCreditCardsActivity({ accountId: card?.accountId }));
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingStatements(false);
    }
  }

  useEffect(() => {
    loadStatementAndActivities();
  }, [card.id]);
  
  const getSpendModeTooltip = (spendModeSummary) => {
    const spendMode = spendModeSummary?.spendMode;
    const txnLimit = convertCentsIntoDollar(spendModeSummary?.transactionCentsLimitPerMerchantPerDay || 0);
    const merchantLimit = spendModeSummary?.merchantLimitPerMonth || 0;

    if (spendMode === "SPEND_LIMIT") {
      return "Spend Limit mode allows the customer to use their card in the classic, spend power manner per billing cycle.";
    } else if (spendMode === "TRANSACTION_LIMIT") {
      return `Transaction Limit mode allows the customer to make up to ${merchantLimit} charges per day of at most ${txnLimit} with at most ${merchantLimit} merchants per month.`;
    } else {
      return "Unknown Spend Mode. Please contact Eng.";
    }
  };

  const createNewCard = async () => {
    if (window.confirm("Are you sure you want to create a new card? Older card(s) will be cancelled.")) {
      await API.createNewCreditCard({ userId });
      refreshList();
    }
  }

  const resetMerchantCycle = async () => {
    try {
      setMerchantCycleLoading(true)
      await API.resetMerchantCycle(userId, account.accountId);
      message.success(`Successfully reset the merchant cycle`);
    } catch (e) {
      message.error('Unable to reset the merchant cycle');
      console.error('Unable reset the merchant cycle', e)
    } finally {setMerchantCycleLoading(false);}
  };

  const deleteMerchantActivationMerchantByAva = async (merchantName) => {
    try {
      await API.deleteMerchantActivationMerchantByAva({userId, merchantName});
      message.success(`Successfully undeleted the merchant`);
      merchantActivation.getMerchantActivation();
    } catch (e) {
      message.error('Unable to undelete the merchant');
      console.error('Unable undelete the merchant', e)
    }
  };

  const handleRefundNegativeBalance = async () => {
    if (!refundAmount || refundAmount <= 0) {
      message.error('Please enter a valid refund amount');
      return;
    }
    const refundAmountCents = Math.round(refundAmount * 100);
    try {
      setRefundLoading(true);
      await API.refundNegativeBalance({
        userId,
        creditCardAccountId: account.accountId,
        refundAmountCents
      });
      message.success('Negative balance refund initiated successfully');
      setRefundModalVisible(false);
      setRefundAmount(null);
      refreshList();
    } catch (ex) {
      message.error(
        ex?.response?.data?.error_message || ex?.response?.data?.errorMessage || 'Failed to process refund'
      );
    } finally {
      setRefundLoading(false);
    }
  };

  const pillColor = (value) => {
    switch (value) {
      case 'ACTIVE':
        return 'success';
      case 'PENDING':
      case 'DELINQUENT':
        return 'warning';
      case 'CHARGED_OFF':
      case 'CANCELLED':
      case 'CLOSED':
        return 'error';
      default:
        return 'default';
    }
  }

  const boolToStr = (foo) => {
    return (foo === true) ? "ON" : (foo === false ? "OFF" : "N/A");
  }

  const errorIfNotSet = (value) => value === null ? "error" : "processing"
  const errorIfSet = (status) => status === null ? "processing" : "error"
  const errorIfFalse = (status) => status === true ? "success" : "error"

  const isTransactionLimitMode = (spendModeSummary) => {
    return (spendModeSummary?.spendMode === "TRANSACTION_LIMIT");
  };

  // Calculate minimum bank balance requirements
  const paymentAmount = statements?.toSorted(
    (a,b) => new Date(b.statementData.statementDate) - new Date(a.statementData.statementDate)
  )?.[0]?.statementData?.statementBalance
    || account?.accountDetails?.paymentDueInCents || 0;
  const autopayMinBalance = Math.max(10000, Math.round(paymentAmount * 1.5));
  const manualPaymentMinBalance = Math.max(5000, Math.round(paymentAmount * 1.5));

  // Current outstanding balance uses the actual net/settled balance (not the amount-due field,
  // which the backend floors to $0 for credit balances). A negative net balance is a credit
  // the user is owed, so surface it explicitly as "$X.XX credit" rather than a bare "-$X.XX".
  const currentBalanceCents = domain?.domain?.balances?.currentBalance?.netBalance?.centsValue;
  const currentBalanceDisplay = currentBalanceCents < 0
    ? `${convertCentsIntoDollar(Math.abs(currentBalanceCents))} credit`
    : convertCentsIntoDollar(currentBalanceCents);

  const lastStatement = statements?.[0] || {};
  const lastFour = domain?.card?.[0]?.lastFour;
  const data = (activity?.activities || [])
    .filter((activity) => activity.success)
    .map((activity) => {
      return {
        debit: activity.item.creditCardStatementDataEntityType === "PAYMENT" || activity.item.amount < 0,
        transactionTime: activity.time,
        descriptor: activity.item.descriptor,
        amountInCents: activity.item.amount > 0 ? activity.item.amount : activity.item.amount * -1,
        balance: activity.item.balance
      }
    })

  const merchants = [...(merchantActivation?.merchantsList || []), ...(merchantActivation?.deletedMerchants || [])];

  return (
    <>
      {/* Flex-wrap so the card table drops below the account badges when they don't both fit; the table
          scrolls sideways inside its own box rather than overflowing. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px' }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <Space direction="vertical">
            <Row>
              <Data isTag tagColor="processing" title="Provider" value={domain?.account?.creditCardProvider} />
              <Data isTag tagColor={pillColor(domain?.account?.accountStatus)} title="Account Status" value={domain?.account?.accountStatus} />
              <Data isTag tagColor="processing" title="Creation Date" value={formatDate(domain?.account?.createTs)} />
              <Data isTag tagColor={errorIfSet(domain?.account?.closureDate)} title="Closure Date" value={domain?.account?.accountClosureTs ? (domain?.account?.accountClosureTs) : null} />
              <Data isTag tagColor={errorIfSet(domain?.account?.delinquentDate)} title="Delinquent Date" value={domain?.account?.accountDelinquentTs ? (domain?.account?.accountDelinquentTs) : null} />
              <Data isTag tagColor={errorIfSet(domain?.account?.chargeoffDate)} title="Charge off Date" value={domain?.account?.accountChargeoffTs ? (domain?.account?.accountChargeoffTs) : null} />
              <Data isTag tagColor={errorIfFalse(!(autoPayStatus?.isCreditCardAutoPayDisabled == true))} title="Autopay Status" value={boolToStr(!(autoPayStatus?.isCreditCardAutoPayDisabled == true))} />
              <Data isTag tagColor={autoPayStatusData.isDisabled ? "error" : "success"} title="Autopay Date" value={autoPayStatusData.autoPayDay} />
            </Row>
            <Text type="secondary" italic>- Account</Text>
          </Space>
        </div>
        <div style={{ flex: '1 1 480px', minWidth: 0 }}>
          <Typography.Text style={{ display: 'block' }} type="secondary" strong><small>Card(s)</small></Typography.Text>
          <Table
            size="small"
            bordered
            pagination={false}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            dataSource={domain?.card || []}
            locale={{ emptyText: 'No cards found' }}
            columns={[
              { title: 'Status', dataIndex: 'cardStatus', key: 'cardStatus', render: (status) => <Tag color={pillColor(status)}>{status}</Tag> },
              { title: 'Last Four', dataIndex: 'lastFour', key: 'lastFour' },
              { title: 'Created', dataIndex: 'createTs', key: 'createTs', render: (ts) => (ts ? formatDate(ts) : '—') },
              { title: 'Exp', key: 'exp', render: (_, card) => (card.expMonth && card.expYear ? `${card.expMonth} / ${card.expYear}` : '—') },
              { title: 'Closed', dataIndex: 'cardClosureTs', key: 'cardClosureTs', render: (ts) => (ts ? formatDate(ts) : '—') },
              { title: 'Closure Reason', dataIndex: 'cardClosureReason', key: 'cardClosureReason', render: (reason) => (reason ? <small>{reason}</small> : '—') },
              { title: 'Provider', dataIndex: 'creditCardProvider', key: 'creditCardProvider', render: (provider) => provider || '—' },
            ]}
          />
        </div>
      </div>
      <Row style={{ marginTop: '40px'}}>
        <Col span="4">
          <Data title="Current outstanding balance" >
            <Title>
              {currentBalanceDisplay}
            </Title>
          </Data>
        </Col>
        <Col span="4">
          <Data title="Current Amount Due" >
            <Title>
              {convertCentsIntoDollar(domain?.domain?.balances?.currentAmountDue)} 
              <br />
              {account?.accountDetails?.paymentDueInCents > 0 && <Data isTag tagColor="warning" title="Due" value={formatDate(account?.accountDetails?.dueDate)} /> }
              {account?.accountDetails?.isDelinquent && <Data isTag tagColor="error" value="Delinquent" /> }
            </Title>
          </Data>
        </Col>
        <Col span="4">
          <Data title="Amount Spent" >
            <Title>{convertCentsIntoDollar(account?.accountDetails?.amountSpentInCents)}</Title>
          </Data>
        </Col>
        <Col span="4">
          <Data title="Max Balance" >
            <Title>
              {convertCentsIntoDollar(account?.maxAccountBalance?.balanceInCents)}
              <br />
              {account?.maxAccountBalance?.balanceInCents > 0 && <Data isTag tagColor="processing" title="Date" value={formatDate(account?.maxAccountBalance?.balanceDate)} /> }
            </Title>
          </Data>
        </Col>
        <Col span="4">
          <Data title="Payment Due Day" >
            <Title>
              {domain?.account?.userRequestedPaymentDueDay === null && <>{domain?.account?.paymentDueDay}</>}
              {domain?.account?.userRequestedPaymentDueDay !== null && <>
                <Text delete disabled>{domain?.account?.paymentDueDay}</Text>&nbsp;
                <Text>{domain?.account?.userRequestedPaymentDueDay}</Text>
              </>}
              <br />
              {domain?.account?.dueDaySetByUser && <Data isTag tagColor="processing" title="Modified" value={formatDate(domain?.account?.dueDaySetTs)} /> }
            </Title>
          </Data>
        </Col>
        <Col span="4">
          <Data title="Billing Day" >
            <Title>
              {domain?.account?.billingStatementDay}
            </Title>
          </Data>
        </Col>
      </Row>
      <Row>
        <Col span="4">
          <Data title="Credit Limit" >
            <Title>{convertCentsIntoDollar(account?.creditCardMetaData?.creditLimitInCents)}</Title>
            <CliEligibilitySummary creditCard={domain} />
          </Data> 
        </Col>
        <Col span="4">
          <Data title="Spend Power (Used)" >
            <Title >{convertCentsIntoDollar(account?.spendPowerUsed)}</Title>
            <Data isTag tagColor="processing" title="Spend Power" value={convertCentsIntoDollar(account?.spendPowerSummary?.totalSpendPowerCents)} /><br/>
            <Data isTag tagColor="processing" title="Spend Power (Available)" value={convertCentsIntoDollar(account?.spendPowerAvailable)} />
          </Data>
        </Col>
        <Col span="4">
          <Data title="Spend Mode" >
            <Title level={4}>{account?.spendModeSummary?.spendMode}</Title>
            <small><Text type="secondary">{getSpendModeTooltip(account?.spendModeSummary)}</Text></small>
            <div style={{marginTop: '1em'}}>
              <Data isTag tagColor='processing' title="Updated" >{formatDate(account?.spendModeSummary.modeUpdateDate)}</Data>
            </div>
          </Data>
        </Col>
        <Col span="4">
          {isTransactionLimitMode(account?.spendModeSummary) && <Data title="Max Merchants Per Day (+ per Month)" >
            <Title>{account?.spendModeSummary?.merchantLimitPerMonth}</Title>
          </Data>}
        </Col>
        <Col span="4">
          {isTransactionLimitMode(account?.spendModeSummary) && <Data title="Transaction Limit" >
            <Title>{convertCentsIntoDollar(account?.spendModeSummary?.transactionCentsLimitPerMerchantPerDay)}</Title>
          </Data>}
        </Col>
      </Row>
      <Row>
        <Col span="4">
          <Data title="Min Balance for Autopay" >
            <Title>
              {convertCentsIntoDollar(autopayMinBalance)}
            </Title>
            <small>Max of 150% balance due or $100</small>
          </Data>
        </Col>
        <Col span="4">
          <Data title="Min Balance for Manual Payment" >
            <Title>
              {convertCentsIntoDollar(manualPaymentMinBalance)}
            </Title>
            <small>Max of 150% balance due or $50</small>
          </Data>
        </Col>
        <Col span="16">
          <Data title="Merchant Activation" >
            <Row>
              <Col span="8">
                  <Title>
                    {merchantActivation.isEnrolled ? "Enrolled" : "Not Enrolled"}
                  </Title>
              </Col>
              <Col span="16">
                {/* Same look as Data's title; Data itself would wrap the table in a Typography span. */}
                <Typography.Text style={{ display: 'block' }} type="secondary" strong><small>Merchants</small></Typography.Text>
                {/* Flex-wrap, not grid breakpoints: wraps when the table's own width no longer fits beside the
                    spend limit. A fixed half (or a viewport breakpoint) let the table overflow into it. */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px' }}>
                  <div style={{ flex: '1 0 auto' }}>
                    <div>
                      <Table
                        size="small"
                        bordered
                        pagination={false}
                        rowKey="merchantGroupName"
                        dataSource={merchants}
                        locale={{ emptyText: 'No merchants found' }}
                        // No per-merchant amount: spend limits are account-level since AVADEV-4175.
                        columns={[
                          { title: 'Merchant', dataIndex: 'displayName', key: 'displayName' },
                          {
                            title: 'Status',
                            key: 'status',
                            render: (_, merchant) => {
                              if (merchant.deletedByAva) return <Typography.Text type="secondary">Deleted by Ava</Typography.Text>;
                              if (merchant.isDeleted) return <Typography.Text type="secondary">Deleted</Typography.Text>;
                              return 'Active';
                            },
                          },
                          {
                            title: '',
                            key: 'action',
                            render: (_, merchant) => {
                              if (!isSupportAgent2()) return null;
                              if (merchant.deletedByAva) {
                                return <Button type="link" size="small" onClick={() => deleteMerchantActivationMerchantByAva(merchant.merchantGroupName)}>Undelete</Button>;
                              }
                              if (!merchant.isDeleted) {
                                return <Button type="link" size="small" danger onClick={() => merchantActivation.deleteMerchant(merchant)}>Delete</Button>;
                              }
                              return null;
                            },
                          },
                        ]}
                      />
                      { isSupportAgent2() && <Button
                        type="link"
                        style={{ paddingLeft: 0 }}
                        onClick={() => setMerchantListModalVisible(true)}
                      >
                        View All ({merchantActivation.merchantToAddList?.length || 0})
                      </Button> }
                    </div>
                  </div>
                  <div style={{ flex: '1 0 200px' }}>
                    <Data title="Merchant Spend Limit (shared by all merchants)" >
                      {convertCentsIntoDollar(merchantActivation.totalSpendLimit)}
                    </Data>
                  </div>
                </div>
              </Col>
            </Row>

          </Data>
        </Col>
      </Row>
      <Divider />
      <Row>
        <Col span="8" offset="16">
            <RangePicker
              value={[startDate, endDate]}
              onChange={(dates) => {
                setStartDate(dates[0]);
                setEndDate(dates[1]);
              }}
            />
             {<PDFDownloadLink
                document={
                  <Document>
                    <CCStatement
                      title="Ava Credit Builder Card Statement"
                      summaryData={userSummary}
                      startDate={startDate}
                      endDate={endDate}
                      last4={lastFour}
                      balanceDue={"$0.00"}
                      list={data || []} />
                  </Document>
                }
                fileName={`Ava Statement, ${moment(startDate).format('MMM-DD-YYYY')} - ${moment(endDate).format('MMM-DD-YYYY')}`}
              >
                {({ documentLoading }) =>
                  documentLoading ? (
                    <Button>Downloading Statement...</Button>
                  ) : (
                    <Button >Download Statement</Button>
                  )
                }
              </PDFDownloadLink>}
        </Col>
      </Row>
        <Title level={5} underline>Last Statement</Title>
        {
          lastStatement?.statementId && (
            <StatementView statement={lastStatement} />
          )
        }
      <Divider />


      <Row className={'card-bottom-buttons-container'}>
        {isSupportLead() && (
          <UpdateSpendMode
            userId={userId}
            accountId={account.accountId}
            refreshList={refreshList}
          />
        )}

        {isSupportAgent3() && (
          <Button
            type={'primary'}
            className={'card-bottom-buttons'}
            onClick={resetMerchantCycle}
            loading={merchantCycleLoading}>
            Reset Merchant Cycle
          </Button>
        )}

        {isSupportAgent2() && account?.cardStatus !== 'CANCELLED' &&
          <Button
            type={'primary'}
            className={'card-bottom-buttons'}
            onClick={() => openChangeDueDateModal(account)}>
            {'Change Due Day'}
          </Button>
        }

        {isSupportAgent2() &&
            <Button
                type={'primary'}
                className={'card-bottom-buttons'}
                onClick={() => showChargeSilaModal(account)}>
              {'Retry Payment'}
            </Button>
        }

        { isSupportManager() && 
            <PaymentLinkCheckoutModal refreshList={refreshList}
              userId={userId}
              ccAccountId={account.accountId}
              ccData={account}/>
        }

      </Row>
      <Row className={'card-bottom-buttons-container'}>
        {isSupportManager() && <VoidTransactionsModal refreshList={refreshList}
          creditCardTransactions={account.creditCardTransactions}
        />}

        {isSupportAgent2() && account.cardStatus !== SUBSCRIPTION_STATUS.CANCELLED && <Button
          className={'card-bottom-buttons'}
          type={'danger'}
          onClick={() => showCancelPaymentModal(account)}>
          {'Close Account'}
        </Button>}

        {isSupportAgent2() && account.cardStatus !== SUBSCRIPTION_STATUS.CANCELLED && <Button
          className={'card-bottom-buttons'}
          type={'danger'}
          onClick={() => createNewCard()}>
          {'Create New Card'}
        </Button>}

        {(isSupportLead() || isSupportManager()) && <Button
          className={'card-bottom-buttons'}
          type={'primary'}
          onClick={() => setRefundModalVisible(true)}>
          {'Refund Negative Balance'}
        </Button>}

        {isSupportAgent2() && <AutoPayAction 
          autoPayStatusEnabled={!(autoPayStatus?.isCreditCardAutoPayDisabled == true)} 
          refreshList={refreshList} 
          type={PAYMENT_PRODUCT_TYPE.CREDIT_CARD} 
          userId={userId} 
          originalAutoPayDay={autoPayStatusData?.autoPayDay}
        />}
      </Row>

      <Row style={{ marginLeft: 20, marginBottom: 20 }}>
        <Col xs={24} xl={24} sm={24} md={24}>
          <PersistedCollapse name={`card-sections:${account?.id}`}>
            <Panel header="Statements" key="1">
              <StatementsList
                statements={statements}
              />
            </Panel>
            <Panel header="Payment History" key="2">
              <PaymentHistoryComponent
                payments={account.creditCardPayments}
              />
            </Panel>
            <Panel header="Failed Payment Attempts" key="3">
              <PaymentAttemptsComponent accountId={card?.accountId} paymentAttempts={paymentAttempts} />
            </Panel>
            <Panel header="Transaction History" key="4">
              <TransactionHistoryComponent
                transactions={account.creditCardTransactions}
                activities={activity?.activities || []}
              />
            </Panel>
            <Panel header="Declined Transaction" key="5">
              <DeclinedTransactionsComponent
                declinedTransactions={account.declinedTransactions}
              />
            </Panel>
            <Panel header="Credit Limit Details (CLI)" key="6">
              <CreditLimitIncentivesComponent
                  accountId={account?.id}
                  domain={domain}
                  creditLimitChanges={account.creditLimitChanges}
                  baseCreditLimit={account.baseCreditLimit}
              />
            </Panel>
            <Panel header="Spend Power Breakdown" key="7">
              {account?.spendLimitChanges && account.spendLimitChanges.length > 0 ? (
                  <SpendLimitChangesComponent spendLimitChanges={account.spendLimitChanges} domain={domain}/>
              ) : (
                  <MonthlySpendPowerComponent
                      accountId={account?.id}
                      data={account?.spendPowerSummary}
                      boostsPending={account?.boostsPending}
                      spendLimitChanges={account?.spendLimitChanges}
                  />
              )}
            </Panel>
            <Panel header="Active Subscriptions" key="8">
              <ActiveSubscriptions
                activeSubscriptions={account?.activeSubscriptions}
              />
            </Panel>
            <Panel header="Payment Links" key="9">
              <PaymentLinkPanel
                userId={userId}
              />
            </Panel>
          </PersistedCollapse>
        </Col>
      </Row>

      <MerchantListModal
        open={merchantListModalVisible}
        onClose={() => setMerchantListModalVisible(false)}
        merchants={merchantActivation.merchantToAddList}
        totalSpendLimit={merchantActivation.totalSpendLimit}
        minSpendLimit={merchantActivation.minSpendLimit}
        userId={userId}
        onAdded={merchantActivation.getMerchantActivation}
      />

      <Modal
        title="Refund Negative Balance"
        open={refundModalVisible}
        onOk={handleRefundNegativeBalance}
        onCancel={() => { setRefundModalVisible(false); setRefundAmount(null); }}
        confirmLoading={refundLoading}
        okText="Refund"
      >
        <p>Enter the refund amount in dollars:</p>
        <InputNumber
          min={1}
          precision={2}
          prefix="$"
          style={{ width: '100%' }}
          value={refundAmount}
          onChange={setRefundAmount}
          placeholder="e.g. 25.00"
        />
      </Modal>
    </>
  )
}